import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LegalContent } from '../components/legal/LegalContent';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { isPasswordValid } from '../lib/passwordPolicy';
import { COUNTRIES } from '../lib/countries';
import { TERMS_MARKDOWN, PRIVACY_MARKDOWN } from '../content/legalContent';
import { Loader2, AlertCircle, CheckCircle2, FileText, ShieldCheck, KeyRound, Building2, Eye, EyeOff, ArrowRight, ArrowLeft, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FULL_STEPS = [
  { key: 'terms', label: 'Terms', icon: FileText },
  { key: 'privacy', label: 'Privacy', icon: ShieldCheck },
  { key: 'password', label: 'Password', icon: KeyRound },
  { key: 'contact', label: 'Contact', icon: Building2 },
];
// Chat-only logins (Drivers/Staff/Passengers) skip the Contact step — their
// contact details are already captured by the operator on the People page.
const CHAT_STEPS = FULL_STEPS.filter((s) => s.key !== 'contact');

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { establishSession } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);

  const [step, setStep] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  // Compliance: the Accept checkbox stays disabled until the user has scrolled
  // through the entire legal document on each of the Terms and Privacy steps.
  const [termsScrolledEnd, setTermsScrolledEnd] = useState(false);
  const [privacyScrolledEnd, setPrivacyScrolledEnd] = useState(false);
  const legalScrollRef = useRef(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [contact, setContact] = useState({
    full_name: '', company: '', address_street: '', address_city: '', address_postcode: '',
    address_state: '', address_country: 'Australia', phone: '', mobile: '',
    display_name_preference: 'personal',
  });
  const setContactField = (k, v) => setContact((p) => ({ ...p, [k]: v }));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const validate = async () => {
      if (!token) { setStatus('error'); setErrorMsg('No activation token provided.'); return; }
      try {
        const res = await axios.get(`${API_URL}/api/auth/invite/${token}`);
        setInvite(res.data);
        // Pre-fill the Contact step with any details the admin already captured for this
        // customer, so they don't re-enter known data (they can still edit/clear anything).
        setContact((p) => ({
          ...p,
          full_name: res.data.full_name || '',
          company: res.data.company || '',
          address_street: res.data.address_street || '',
          address_city: res.data.address_city || '',
          address_postcode: res.data.address_postcode || '',
          address_state: res.data.address_state || '',
          address_country: res.data.address_country || 'Australia',
          phone: res.data.phone || '',
          mobile: res.data.mobile || '',
          display_name_preference: res.data.display_name_preference || 'personal',
        }));
        setStatus('ready');
      } catch (e) {
        setStatus('error');
        setErrorMsg(e.response?.data?.detail || 'This activation link is invalid or has expired.');
      }
    };
    validate();
  }, [token]);

  // On entering the Terms or Privacy step, scroll the document back to the top and
  // require the user to scroll to the bottom before they can accept. If the document
  // is short enough that it doesn't overflow, there's nothing to scroll so allow it.
  useEffect(() => {
    if (status !== 'ready') return;
    const stepsArr = invite?.is_chat_contact ? CHAT_STEPS : FULL_STEPS;
    const key = stepsArr[step]?.key;
    if (key !== 'terms' && key !== 'privacy') return;
    const el = legalScrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const noOverflow = el.scrollHeight <= el.clientHeight + 4;
    if (noOverflow) {
      if (key === 'terms') setTermsScrolledEnd(true);
      else setPrivacyScrolledEnd(true);
    }
  }, [step, status, invite]);

  const passwordWeak = password.length > 0 && !isPasswordValid(password);
  const passwordsMismatch = confirm.length > 0 && password !== confirm;
  const passwordReady = isPasswordValid(password) && password === confirm;
  const fullNameValid = !!(invite?.is_chat_contact || (contact.full_name && contact.full_name.trim()));
  const canActivate = passwordReady && acceptedTerms && acceptedPrivacy && fullNameValid;

  const steps = invite?.is_chat_contact ? CHAT_STEPS : FULL_STEPS;

  const goNext = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleActivate = async () => {
    setSubmitError('');
    if (!canActivate) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/invite/${token}/accept`, {
        password,
        accept_terms: acceptedTerms,
        accept_privacy: acceptedPrivacy,
        ...(invite?.is_chat_contact ? {} : {
          ...contact,
          display_name_preference: (contact.company || '').trim() ? (contact.display_name_preference || 'personal') : 'personal',
        }),
      });
      const { token: newToken, user } = res.data;
      establishSession(newToken, user);
      setStatus('success');
      setTimeout(() => navigate('/', { replace: true }), 900);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || 'Activation failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center" data-testid="invite-loading">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Verifying your activation link…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl max-w-md w-full text-center" data-testid="invite-error">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Activation link problem</h2>
          <p className="text-slate-300 mb-6">{errorMsg}</p>
          <Button onClick={() => navigate('/login', { replace: true })} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="invite-go-login-btn">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center" data-testid="invite-success">
          <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Account activated!</h2>
          <p className="text-slate-300">Signing you in…</p>
        </div>
      </div>
    );
  }

  const isTermsStep = steps[step].key === 'terms';
  const isPrivacyStep = steps[step].key === 'privacy';
  const isPasswordStep = steps[step].key === 'password';
  const isContactStep = steps[step].key === 'contact';
  const isLastStep = step === steps.length - 1;
  const nextDisabled =
    (isTermsStep && !acceptedTerms) ||
    (isPrivacyStep && !acceptedPrivacy) ||
    (isPasswordStep && !passwordReady);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]" data-testid="accept-invite-wizard">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-slate-100 dark:border-slate-800">
          <p className="text-xs font-semibold tracking-wide text-[#2563eb] uppercase mb-1">Welcome to EnsueLIVETRAK</p>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Set up your account</h1>
          {invite?.email && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Activating <span className="font-medium text-slate-700 dark:text-slate-200" data-testid="invite-email">{invite.email}</span>
            </p>
          )}

          {/* Stepper */}
          <div className="flex items-center gap-2 mt-5" data-testid="invite-stepper">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const active = i === step;
              const done = i < step;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors
                      ${done ? 'bg-green-500 text-white' : active ? 'bg-[#2563eb] text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                      {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${active ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
          {(isTermsStep || isPrivacyStep) && (() => {
            const scrolledEnd = isTermsStep ? termsScrolledEnd : privacyScrolledEnd;
            return (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                Please read the {isTermsStep ? 'Terms & Conditions' : 'Privacy Policy'} below, then confirm your acceptance to continue.
              </p>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 max-h-[42vh] overflow-y-auto bg-slate-50 dark:bg-slate-800/40"
                   ref={legalScrollRef}
                   onScroll={(e) => {
                     const el = e.currentTarget;
                     if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
                       if (isTermsStep) setTermsScrolledEnd(true);
                       else setPrivacyScrolledEnd(true);
                     }
                   }}
                   data-testid={isTermsStep ? 'terms-scroll' : 'privacy-scroll'}>
                <LegalContent markdown={isTermsStep ? TERMS_MARKDOWN : PRIVACY_MARKDOWN} />
              </div>
              {!scrolledEnd && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mt-2" data-testid={isTermsStep ? 'terms-scroll-hint' : 'privacy-scroll-hint'}>
                  <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
                  Please scroll to the bottom to read the full {isTermsStep ? 'Terms & Conditions' : 'Privacy Policy'} before accepting.
                </p>
              )}
              <label className={`flex items-start gap-3 mt-4 select-none ${scrolledEnd ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                <Checkbox
                  checked={isTermsStep ? acceptedTerms : acceptedPrivacy}
                  disabled={!scrolledEnd}
                  onCheckedChange={(v) => (isTermsStep ? setAcceptedTerms(!!v) : setAcceptedPrivacy(!!v))}
                  className="mt-0.5"
                  data-testid={isTermsStep ? 'accept-terms-checkbox' : 'accept-privacy-checkbox'}
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  I have read and accept the <strong>{isTermsStep ? 'Terms & Conditions' : 'Privacy Policy'}</strong>.
                </span>
              </label>
            </>
            );
          })()}

          {isPasswordStep && (
            <div className="space-y-4 max-w-md">
              <p className="text-sm text-slate-600 dark:text-slate-300">Create a password to finish activating your account.</p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">New password</label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a strong password"
                    className="pr-10"
                    data-testid="invite-password-input"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordRequirements password={password} className="pt-1" />
                {passwordWeak && <p className="text-xs text-red-500">Password does not meet the requirements above.</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Confirm password</label>
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                  data-testid="invite-confirm-input"
                />
                {passwordsMismatch && <p className="text-xs text-red-500">Passwords do not match.</p>}
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700 p-3 text-xs text-slate-500 dark:text-slate-400 space-y-1">
                <p className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Terms & Conditions accepted</p>
                <p className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Privacy Policy accepted</p>
              </div>
              {submitError && <p className="text-sm text-red-500" data-testid="invite-submit-error">{submitError}</p>}
            </div>
          )}

          {isContactStep && (
            <div className="space-y-4" data-testid="invite-contact-step">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Edit or Add your contact details so we can keep your account up to date. You can change these anytime in your profile.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Full name <span className="text-red-500">*</span></label>
                <Input
                  value={contact.full_name}
                  onChange={(e) => setContactField('full_name', e.target.value)}
                  placeholder="Full name"
                  data-testid="invite-fullname-input"
                  className={fullNameValid ? '' : 'border-red-400 focus-visible:ring-red-400'}
                />
                {!fullNameValid && <p className="text-xs text-red-500" data-testid="invite-fullname-error">Full name is required.</p>}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Company name</label>
                <Input value={contact.company} onChange={(e) => setContactField('company', e.target.value)} placeholder="Company name" data-testid="invite-company-input" />
              </div>
              {(contact.company || '').trim() && (
                <div className="space-y-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700 p-3" data-testid="invite-display-preference">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Display my account as</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                      <input
                        type="radio"
                        name="display_name_preference"
                        checked={(contact.display_name_preference || 'personal') === 'personal'}
                        onChange={() => setContactField('display_name_preference', 'personal')}
                        data-testid="invite-display-personal"
                      />
                      Personal name{contact.full_name ? ` (${contact.full_name})` : ''}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                      <input
                        type="radio"
                        name="display_name_preference"
                        checked={contact.display_name_preference === 'company'}
                        onChange={() => setContactField('display_name_preference', 'company')}
                        data-testid="invite-display-company"
                      />
                      Company name ({contact.company.trim()})
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Chooses which name represents your account.</p>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Street address</label>
                <Input value={contact.address_street} onChange={(e) => setContactField('address_street', e.target.value)} placeholder="Street address" data-testid="invite-street-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">City / Suburb</label>
                  <Input value={contact.address_city} onChange={(e) => setContactField('address_city', e.target.value)} placeholder="City / Suburb" data-testid="invite-city-input" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Postcode</label>
                  <Input value={contact.address_postcode} onChange={(e) => setContactField('address_postcode', e.target.value)} placeholder="Postcode" data-testid="invite-postcode-input" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">State / Region</label>
                  <Input value={contact.address_state} onChange={(e) => setContactField('address_state', e.target.value)} placeholder="State / Region" data-testid="invite-state-input" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Country</label>
                  <Select value={contact.address_country} onValueChange={(v) => setContactField('address_country', v)}>
                    <SelectTrigger data-testid="invite-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Phone</label>
                  <Input value={contact.phone} onChange={(e) => setContactField('phone', e.target.value)} placeholder="Phone" data-testid="invite-phone-input" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Mobile</label>
                  <Input value={contact.mobile} onChange={(e) => setContactField('mobile', e.target.value)} placeholder="Mobile" data-testid="invite-mobile-input" />
                </div>
              </div>
              {submitError && <p className="text-sm text-red-500" data-testid="invite-submit-error-contact">{submitError}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={goBack} disabled={step === 0 || submitting} data-testid="invite-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {!isLastStep ? (
            <Button onClick={goNext} disabled={nextDisabled} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="invite-next-btn">
              Continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleActivate} disabled={!canActivate || submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="invite-activate-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Finish & Activate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
